import Skeleton from '@/components/atoms/Skeleton/Skeleton';
import { useLivescoreMatch } from '@/context/LivescoreMatchContext';
import { cn } from '@/utils/cn';
import { calculateXG } from '@/utils/xG';
import { useEffect, useState } from 'react';

const Bar = ({
  percent,
  team,
  isWinning,
  animate,
}: {
  percent: number;
  team: 'home' | 'away';
  isWinning: boolean;
  animate?: boolean;
}) => (
  <div
    className="rounded-[13px] overflow-hidden dark:bg-black-400 bg-white_theme-100 relative w-full h-1.5"
    aria-hidden
  >
    <div
      className={cn(
        'absolute h-full transition-all duration-500 ease-out',
        team === 'home'
          ? isWinning
            ? 'bg-[#53F293]'
            : 'bg-[#53F293]/40'
          : isWinning
          ? 'bg-[#6960EE]'
          : 'bg-[#6960EE]/40',
      )}
      style={{
        width: animate ? `${percent}%` : '0%',
        [team === 'home' ? 'right' : 'left']: 0,
      }}
    />
  </div>
);

interface StatisticBarProps {
  title: string;
  loading?: boolean;
  noValue?: boolean;
  values: {
    home: {
      displayValue: string;
      value: number;
      customPercent?: number;
    };
    away: {
      displayValue: string;
      value: number;
      customPercent?: number;
    };
  };
  animate?: boolean;
}

const StatisticBar = ({ title, loading, noValue, values, animate }: StatisticBarProps) => (
  <div className="text-black dark:text-white space-y-2">
    <div className="flex items-end justify-between">
      {loading ? (
        <Skeleton className="w-2 h-4" />
      ) : (
        <p className="font-semibold sm:text-[13px] text-[10px]">{noValue ? '-' : values.home.displayValue}</p>
      )}
      <p className="sm:text-[12px] text-[9px] font-medium mb-1">{title}</p>
      {loading ? (
        <Skeleton className="w-2 h-4" />
      ) : (
        <p className="font-semibold sm:text-[13px] text-[10px]">{noValue ? '-' : values.away.displayValue}</p>
      )}
    </div>
    <div className="flex gap-6">
      {loading ? (
        <div className="flex gap-6 w-full">
          <Skeleton className="w-full h-1.5" />
          <Skeleton className="w-full h-1.5" />
        </div>
      ) : (
        <>
          <Bar
            percent={
              noValue
                ? 0
                : values.home.customPercent ?? (values.home.value * 100) / (values.home.value + values.away.value)
            }
            team="home"
            isWinning={values.home.value > values.away.value}
            animate={animate}
          />
          <Bar
            percent={
              noValue
                ? 0
                : values.away.customPercent ?? (values.away.value * 100) / (values.home.value + values.away.value)
            }
            team="away"
            isWinning={values.away.value > values.home.value}
            animate={animate}
          />
        </>
      )}
    </div>
  </div>
);

const LivescoreMatchStatistics = () => {
  const { match, timeline, timelineLoading } = useLivescoreMatch();
  const [homeXG, setHomeXG] = useState(0);
  const [awayXG, setAwayXG] = useState(0);
  const [areStatsReady, setAreStatsReady] = useState(false);

  useEffect(() => {
    if (!timelineLoading && Array.isArray(timeline) && timeline?.length) {
      setAreStatsReady(false);

      const homeTimeline = timeline.filter((event) => event.competitor === 'home');
      const awayTimeline = timeline.filter((event) => event.competitor === 'away');

      const homeXGRaw = calculateXG(homeTimeline, 'home');
      const awayXGRaw = calculateXG(awayTimeline, 'away');

      const homeStats = match?.statistics?.totals?.competitors?.[0]?.statistics;
      const awayStats = match?.statistics?.totals?.competitors?.[1]?.statistics;

      const calcQualityFactor = (stats: typeof homeStats) => {
        if (!stats) return 1;
        const shotAcc = stats.shots_on_target && stats.shots_total
          ? stats.shots_on_target / stats.shots_total : 0.5;
        const possession = stats.ball_possession != null
          ? stats.ball_possession / 100 : 0.5;
        const passAcc = stats.passes_successful && stats.passes_total
          ? stats.passes_successful / stats.passes_total : 0.5;
        return shotAcc * 0.35 + possession * 0.65 + passAcc * 0.15;
      };

      const homeAccuracy = calcQualityFactor(homeStats);
      const awayAccuracy = calcQualityFactor(awayStats);

      if (homeXGRaw !== null) setHomeXG(parseFloat((homeXGRaw * homeAccuracy).toFixed(2)));
      if (awayXGRaw !== null) setAwayXG(parseFloat((awayXGRaw * awayAccuracy).toFixed(2)));

      setAreStatsReady(true);
    }
  }, [timeline]);

  if (!match.statistics?.totals?.competitors) {
    return <p className="text-xxs text-gray-400">Brak statystyk dla tego meczu</p>;
  }

  return (
    <ul className="list-none pl-0 sm:space-y-6 space-y-4 py-2">
      <li>
        <StatisticBar
          title="Oczekiwane gole (xG)"
          values={{
            home: { displayValue: homeXG.toFixed(2), value: homeXG },
            away: { displayValue: awayXG.toFixed(2), value: awayXG },
          }}
          loading={timelineLoading}
          noValue={!timelineLoading && (!Array.isArray(timeline) || !timeline?.length)}
          animate={areStatsReady}
        />
      </li>
      {match?.statistics?.totals?.competitors?.[0]?.statistics?.ball_possession &&
      match?.statistics?.totals?.competitors?.[1]?.statistics?.ball_possession ? (
        <li>
          <StatisticBar
            title="Posiadanie piłki"
            values={{
              home: {
                displayValue: `${match?.statistics?.totals?.competitors?.[0]?.statistics?.ball_possession}%`,
                value: match?.statistics?.totals?.competitors?.[0]?.statistics?.ball_possession,
              },
              away: {
                displayValue: `${match?.statistics?.totals?.competitors?.[1]?.statistics?.ball_possession}%`,
                value: match?.statistics?.totals?.competitors?.[1]?.statistics?.ball_possession,
              },
            }}
            animate={areStatsReady}
          />
        </li>
      ) : null}

      {match?.statistics?.totals?.competitors?.[0]?.statistics?.shots_total &&
      match?.statistics?.totals?.competitors?.[1]?.statistics?.shots_total ? (
        <li>
          <StatisticBar
            title="Strzały Łącznie"
            values={{
              home: {
                displayValue: match?.statistics?.totals?.competitors?.[0]?.statistics?.shots_total.toString(),
                value: match?.statistics?.totals?.competitors?.[0]?.statistics?.shots_total,
              },
              away: {
                displayValue: match?.statistics?.totals?.competitors?.[1]?.statistics?.shots_total.toString(),
                value: match?.statistics?.totals?.competitors?.[1]?.statistics?.shots_total,
              },
            }}
            animate={areStatsReady}
          />
        </li>
      ) : null}

      {match?.statistics?.totals?.competitors?.[0]?.statistics?.shots_on_target &&
      match?.statistics?.totals?.competitors?.[1]?.statistics?.shots_on_target ? (
        <li>
          <StatisticBar
            title="Strzały Celne"
            values={{
              home: {
                displayValue: match?.statistics?.totals?.competitors?.[0]?.statistics?.shots_on_target.toString(),
                value: match?.statistics?.totals?.competitors?.[0]?.statistics?.shots_on_target!,
              },
              away: {
                displayValue: match?.statistics?.totals?.competitors?.[1]?.statistics?.shots_on_target.toString(),
                value: match?.statistics?.totals?.competitors?.[1]?.statistics?.shots_on_target,
              },
            }}
            animate={areStatsReady}
          />
        </li>
      ) : null}

      {match?.statistics?.totals?.competitors?.[0]?.statistics?.corner_kicks &&
      match?.statistics?.totals?.competitors?.[1]?.statistics?.corner_kicks ? (
        <li>
          <StatisticBar
            title="Rzuty rożne"
            values={{
              home: {
                displayValue: match?.statistics?.totals?.competitors?.[0]?.statistics?.corner_kicks.toString(),
                value: match?.statistics?.totals?.competitors?.[0]?.statistics?.corner_kicks!,
              },
              away: {
                displayValue: match?.statistics?.totals?.competitors?.[1]?.statistics?.corner_kicks!.toString(),
                value: match?.statistics?.totals?.competitors?.[1]?.statistics?.corner_kicks!,
              },
            }}
            animate={areStatsReady}
          />
        </li>
      ) : null}

      {match?.statistics?.totals?.competitors?.[0]?.statistics?.passes_total &&
      match?.statistics?.totals?.competitors?.[1]?.statistics?.passes_total &&
      match?.statistics?.totals?.competitors?.[0]?.statistics?.passes_unsuccessful &&
      match?.statistics?.totals?.competitors?.[1]?.statistics?.passes_unsuccessful
        ? (() => {
            const shotsAccuracyHome =
              ((match?.statistics?.totals?.competitors?.[0]?.statistics?.passes_total -
                match?.statistics?.totals?.competitors?.[0]?.statistics?.passes_unsuccessful) /
                match?.statistics?.totals?.competitors?.[0]?.statistics?.passes_total) *
              100;

            const shotsAccuracyAway =
              ((match?.statistics?.totals?.competitors?.[1]?.statistics?.passes_total -
                match?.statistics?.totals?.competitors?.[1]?.statistics?.passes_unsuccessful) /
                match?.statistics?.totals?.competitors?.[1]?.statistics?.passes_total) *
              100;
            return (
              <li>
                <StatisticBar
                  title="Celność podań"
                  values={{
                    home: {
                      displayValue: shotsAccuracyHome.toFixed(0) + '%',
                      value: shotsAccuracyHome,
                      customPercent: shotsAccuracyHome,
                    },
                    away: {
                      displayValue: shotsAccuracyAway.toFixed(0) + '%',
                      value: shotsAccuracyAway,
                      customPercent: shotsAccuracyAway,
                    },
                  }}
                  animate={areStatsReady}
                />
              </li>
            );
          })()
        : null}

      {match?.statistics?.totals?.competitors?.[0]?.statistics?.yellow_cards &&
      match?.statistics?.totals?.competitors?.[1]?.statistics?.yellow_cards ? (
        <li>
          <StatisticBar
            title="Żółte kartki"
            values={{
              home: {
                displayValue: match?.statistics?.totals?.competitors?.[0]?.statistics?.yellow_cards.toString(),
                value: match?.statistics?.totals?.competitors?.[0]?.statistics?.yellow_cards!,
              },
              away: {
                displayValue: match?.statistics?.totals?.competitors?.[1]?.statistics?.yellow_cards!.toString(),
                value: match?.statistics?.totals?.competitors?.[1]?.statistics?.yellow_cards!,
              },
            }}
            animate={areStatsReady}
          />
        </li>
      ) : null}

      {match?.statistics?.totals?.competitors?.[0]?.statistics?.fouls &&
      match?.statistics?.totals?.competitors?.[1]?.statistics?.fouls ? (
        <li>
          <StatisticBar
            title="Faule"
            values={{
              home: {
                displayValue: match?.statistics?.totals?.competitors?.[0]?.statistics?.fouls.toString(),
                value: match?.statistics?.totals?.competitors?.[0]?.statistics?.fouls!,
              },
              away: {
                displayValue: match?.statistics?.totals?.competitors?.[1]?.statistics?.fouls!.toString(),
                value: match?.statistics?.totals?.competitors?.[1]?.statistics?.fouls!,
              },
            }}
            animate={areStatsReady}
          />
        </li>
      ) : null}

      {match?.statistics?.totals?.competitors?.[0]?.statistics?.offsides &&
      match?.statistics?.totals?.competitors?.[1]?.statistics?.offsides ? (
        <li>
          <StatisticBar
            title="Spalone"
            values={{
              home: {
                displayValue: match?.statistics?.totals?.competitors?.[0]?.statistics?.offsides.toString(),
                value: match?.statistics?.totals?.competitors?.[0]?.statistics?.offsides!,
              },
              away: {
                displayValue: match?.statistics?.totals?.competitors?.[1]?.statistics?.offsides!.toString(),
                value: match?.statistics?.totals?.competitors?.[1]?.statistics?.offsides!,
              },
            }}
            animate={areStatsReady}
          />
        </li>
      ) : null}
    </ul>
  );
};

export default LivescoreMatchStatistics;
